import * as React from 'react';
import useStyles from './UsernameForm.styles';
import { Controller, useForm } from 'react-hook-form';
import { TextField } from '../../atoms/TextField';
import { Button } from '../../atoms/Button';
import { Trans } from '@lingui/react';
import { IUser } from '../../../server/withUser';
import { isAllowedEmail } from '../../../utils/isInvalidEmailDomain';

export interface IUsernameFormProps {
  user?: IUser;
  onSubmit: (data: { username: string }) => Promise<void>;
  error: JSX.Element | null;
  buttonText: JSX.Element;
  alternatePlaceholder?: JSX.Element;
  forceEmailInput?: boolean;
  blockInvalidEmailDomains?: boolean;
  isExocadUser?: boolean;
}
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const UsernameForm: React.FC<IUsernameFormProps> = ({
  user,
  onSubmit,
  error,
  buttonText,
  alternatePlaceholder,
  forceEmailInput = false,
  blockInvalidEmailDomains = false,
  isExocadUser = true,
}) => {
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, errors },
  } = useForm({
    criteriaMode: 'all',
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      username: user?.email || '',
    },
  });
  const isInvalidEmail =
    errors.username?.type === 'invalid_email' ? (
      <Trans id="Must be a valid email" />
    ) : null;
  const isInvalidUsername =
    errors.username?.type === 'invalid_username' ? (
      <Trans id="Please provide a valid username." />
    ) : null;
  const isInvalidDomain =
    errors.username?.type === 'invalid_domain' ? (
      <Trans id="This email provider is not supported." />
    ) : null;
  return (
    <div className={classes.root}>
      <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.input}>
          <Controller
            control={control}
            name="username"
            rules={
              forceEmailInput && !blockInvalidEmailDomains
                ? {
                    required: true,
                    validate: {
                      invalid_email: (v: string) => v && emailRegex.test(v),
                    },
                  }
                : forceEmailInput && blockInvalidEmailDomains
                ? {
                    required: true,
                    validate: {
                      invalid_email: (v: string) => v && emailRegex.test(v),
                      invalid_domain: (v: string) => v && isAllowedEmail(v),
                    },
                  }
                : {
                    required: true,
                    validate: {
                      invalid_username: (v: string) =>
                        (v && emailRegex.test(v)) ||
                        /^[a-zA-Z0-9_+-.!#$'^`~@]+$/.test(v),
                    },
                  }
            }
            render={({ field }) => {
              return (
                <TextField
                  {...field}
                  isExocadInput={isExocadUser}
                  error={
                    error ||
                    isInvalidEmail ||
                    isInvalidUsername ||
                    isInvalidDomain
                  }
                  autoFocus={true}
                  placeholder={
                    alternatePlaceholder ? (
                      alternatePlaceholder
                    ) : (
                      <Trans id={'Username*'} />
                    )
                  }
                />
              );
            }}
          />
        </div>
        <div className={classes.submit}>
          <Button
            variant="primary"
            type="submit"
            fullWidth={true}
            disabled={!isValid}
            loading={isSubmitting}
          >
            {buttonText}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UsernameForm;
